// ✅ src/pages/VehicleV2/OwnVehicleReport.jsx
// UPDATED FULL FILE
//
// ✅ Supports preset vehicle type from navigation:
//    navigate("/vehicle/OwnVehicleReport", { state: { vehicleType: r.type } })
// ✅ If vehicleType is passed => auto-select Vehicle Type and DISABLE the dropdown
// ✅ Keeps locked type even on Reset
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + dropdownLoading + listLoading + subTypeLoading
// - prevents repeated initial API calls
// - safer search / reset / pagination / print flow
// - keeps locked type stable

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaInfoCircle,
  FaSyncAlt,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/ownVehicleReport";

/* ---------------- helpers ---------------- */
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
    value: String(x.value ?? x.id ?? x.key ?? fallbackLabel),
    ...x,
  }));

  const hasSelect = list.some((x) => toStr(x.value).toLowerCase() === "select");

  if (!list.length) {
    return [{ label: fallbackLabel, value: fallbackLabel }];
  }

  return hasSelect
    ? list
    : [{ label: fallbackLabel, value: fallbackLabel }, ...list];
};

// dd/MM/yyyy display
const fmtDate = (v) => {
  if (!v) return "";
  return toStr(v);
};

// number display
const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN");
};

const defaultFiltersByRole = (branchType, branchId) =>
  toStr(branchType).toUpperCase() !== "HO"
    ? {
        branchId: String(branchId),
        status: "Select",
        vehicleTypeIndex: "Select",
        vehSubTypeId: "Select",
        storeId: "Select",
        vehicleCode: "",
        make: "",
        model: "",
      }
    : {
        branchId: "Select",
        status: "Select",
        vehicleTypeIndex: "Select",
        vehSubTypeId: "Select",
        storeId: "Select",
        vehicleCode: "",
        make: "",
        model: "",
      };

export default function OwnVehicleReport() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const presetVehicleType = state?.vehicleType;

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toInt(loginUserId, 0),
    }),
    [sessionBranchId, branchType, loginUserId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);
  const presetAppliedRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [subTypeLoading, setSubTypeLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading || subTypeLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);
  const [lockVehicleType, setLockVehicleType] = useState(false);

  /* ===========================
     dropdown states
  =========================== */
  const [branchOptions, setBranchOptions] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [vehicleSubTypeOptions, setVehicleSubTypeOptions] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [storeOptions, setStoreOptions] = useState([
    { value: "Select", label: "Select" },
  ]);

  /* ===========================
     filters + list states
  =========================== */
  const baseDefaultFilters = useMemo(
    () => defaultFiltersByRole(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  const [filters, setFilters] = useState(baseDefaultFilters);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  /* ===========================
     computed
  =========================== */
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const inputHeadClass = useMemo(() => `${buttonClass} text-white`, [buttonClass]);

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  /* ===========================
     helpers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const applyPresetVehicleType = useCallback(
    (list) => {
      if (!presetVehicleType || presetAppliedRef.current) return;

      const found =
        list.find((x) => String(x.value) === String(presetVehicleType)) ||
        list.find(
          (x) =>
            String(x.label).toLowerCase() ===
            String(presetVehicleType).toLowerCase()
        );

      if (found) {
        setFilters((prev) => ({
          ...prev,
          vehicleTypeIndex: String(found.value),
          vehSubTypeId: "Select",
        }));
        setLockVehicleType(true);
        presetAppliedRef.current = true;
      }
    },
    [presetVehicleType]
  );

  /* ===========================
     dropdown load
  =========================== */
  const loadBranches = useCallback(async () => {
    if (toStr(session.branchType).toUpperCase() !== "HO") {
      setBranchOptions([{ value: "Select", label: "Select" }]);
      return;
    }

    try {
      const { data } = await axios.get(`${API_BASE}/branches`, {
        params: {
          branchType: session.branchType,
          branchId: session.branchId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load branches");
      }

      setBranchOptions(normalizeOptionList(data.branches, "Select"));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Failed to load branches");
      setBranchOptions([{ value: "Select", label: "Select" }]);
    }
  }, [session, commonHeaders]);

  const loadVehicleTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/vehicleTypes`, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load vehicle types");
      }

      const list = normalizeOptionList(data.vehicleTypes, "Select");
      setVehicleTypeOptions(list);
      applyPresetVehicleType(list);
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message || e.message || "Failed to load vehicle types"
      );
      setVehicleTypeOptions([{ value: "Select", label: "Select" }]);
    }
  }, [commonHeaders, applyPresetVehicleType]);

  const loadVehicleSubTypes = useCallback(
    async (vehicleTypeIndex) => {
      try {
        setSubTypeLoading(true);

        const { data } = await axios.get(`${API_BASE}/vehicleSubTypes`, {
          params: { vehicleTypeIndex },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load vehicle sub types");
        }

        setVehicleSubTypeOptions(normalizeOptionList(data.subTypes, "Select"));
      } catch (e) {
        console.error(e);
        toast.error(
          e?.response?.data?.message || e.message || "Failed to load vehicle sub types"
        );
        setVehicleSubTypeOptions([{ value: "Select", label: "Select" }]);
      } finally {
        setSubTypeLoading(false);
      }
    },
    [commonHeaders]
  );

  const loadStores = useCallback(
    async (branchIdForStore) => {
      const bId = toInt(branchIdForStore, 0);
      if (!bId) {
        setStoreOptions([{ value: "Select", label: "Select" }]);
        return;
      }

      try {
        const { data } = await axios.get(`${API_BASE}/stores`, {
          params: { branchId: bId },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load stores");
        }

        setStoreOptions(normalizeOptionList(data.stores, "Select"));
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load stores");
        setStoreOptions([{ value: "Select", label: "Select" }]);
      }
    },
    [commonHeaders]
  );

  /* ===========================
     list fetch
  =========================== */
  const fetchList = useCallback(
    async (opts = {}, nextFilters = filters) => {
      try {
        setListLoading(true);

        const targetPage = opts.page ?? page;

        const params = {
          branchType: session.branchType,
          loginUserId: session.loginUserId,
          branchId:
            toStr(session.branchType).toUpperCase() === "HO"
              ? nextFilters.branchId
              : session.branchId,
          status: nextFilters.status,
          vehicleTypeIndex: nextFilters.vehicleTypeIndex,
          vehicleSubTypeId: nextFilters.vehSubTypeId,
          storeId: nextFilters.storeId,
          vehicleCode: nextFilters.vehicleCode,
          make: nextFilters.make,
          model: nextFilters.model,
          page: targetPage,
          pageSize,
          sessionBranchId: session.branchId,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load report");
        }

        setRows(toList(data.rows));
        setPage(toInt(data.page, targetPage));
        setTotal(toInt(data.total, 0));
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setTotal(0);
      } finally {
        setListLoading(false);
      }
    },
    [filters, page, pageSize, session, commonHeaders]
  );

  /* ===========================
     initial load
  =========================== */
  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(baseDefaultFilters);

      await Promise.all([
        loadVehicleTypes(),
        toStr(session.branchType).toUpperCase() === "HO"
          ? loadBranches()
          : Promise.resolve(),
        loadStores(session.branchId),
      ]);
    } finally {
      setPageLoading(false);
    }
  }, [baseDefaultFilters, loadVehicleTypes, loadBranches, loadStores, session.branchId, session.branchType]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      if (lockVehicleType && name === "vehicleTypeIndex") return;

      setFilters((prev) => {
        const next = { ...prev, [name]: value };

        if (name === "vehicleTypeIndex") {
          next.vehSubTypeId = "Select";
        }

        if (name === "branchId") {
          next.storeId = "Select";
        }

        return next;
      });
    },
    [lockVehicleType]
  );

  const handleSearch = useCallback(async () => {
    await fetchList({ page: 1 }, filters);
  }, [fetchList, filters]);

  const handleView = useCallback(async () => {
    await fetchList({ page: 1 }, filters);
  }, [fetchList, filters]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    setRows([]);
    setTotal(0);
    setPage(1);
  }, [loadPage]);

  const handleReset = useCallback(() => {
    const resetBase = defaultFiltersByRole(session.branchType, session.branchId);

    const reset = {
      ...resetBase,
      vehicleTypeIndex: lockVehicleType ? filters.vehicleTypeIndex : "Select",
      vehSubTypeId: "Select",
    };

    setFilters(reset);
    setRows([]);
    setTotal(0);
    setPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId, lockVehicleType, filters.vehicleTypeIndex]);

  const handleRowPrint = useCallback(
    (row) => {
      const vehicleId = row?.fld_vehicleid;
      if (!vehicleId) {
        toast.error("Vehicle ID not found in this row");
        return;
      }

      navigate("/vehicle/OwnEquipmentReport", {
        state: { vehicleId, row },
      });
    },
    [navigate]
  );

  const handlePrint = useCallback(() => {
    const payload = {
      branchType: session.branchType,
      loginUserId: session.loginUserId,
      branchId:
        toStr(session.branchType).toUpperCase() === "HO"
          ? filters.branchId
          : session.branchId,
      status: filters.status,
      vehicleTypeIndex: filters.vehicleTypeIndex,
      vehicleSubTypeId: filters.vehSubTypeId,
      storeId: filters.storeId,
      vehicleCode: filters.vehicleCode,
      make: filters.make,
      model: filters.model,
      pageSize,
      sessionBranchId: session.branchId,
    };

    navigate("/vehicle/OwnVehicleReportPrint", { state: payload });
  }, [navigate, session, filters, pageSize]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const goPage = useCallback(
    async (p) => {
      const next = Math.min(Math.max(1, p), totalPages);
      await fetchList({ page: next }, filters);
    },
    [fetchList, filters, totalPages]
  );

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const maybeType = toStr(filters.vehicleTypeIndex);
    if (!maybeType || isSelect(maybeType)) {
      setVehicleSubTypeOptions([{ value: "Select", label: "Select" }]);
      return;
    }
    loadVehicleSubTypes(maybeType);
  }, [filters.vehicleTypeIndex, loadVehicleSubTypes]);

  useEffect(() => {
    if (toStr(session.branchType).toUpperCase() !== "HO") return;

    const b = isSelect(filters.branchId) ? session.branchId : Number(filters.branchId || 0);
    loadStores(b);
  }, [filters.branchId, session.branchType, session.branchId, loadStores]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">OWN VEHICLE REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Own Vehicle Report
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  Total Records: {total}
                </span>

                {lockVehicleType && (
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      isDark ? "bg-green-900 text-green-100" : "bg-green-100 text-green-800"
                    }`}
                  >
                    Vehicle Type Locked
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Loading...
              </h3>
              <p
                className={`mb-6 max-w-sm mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    {toStr(session.branchType).toUpperCase() === "HO" && (
                      <div>
                        <label className={labelClass}>Branch</label>
                        <select
                          name="branchId"
                          value={filters.branchId}
                          onChange={handleChange}
                          className={inputClass}
                          disabled={isBusy}
                        >
                          {branchOptions.map((b, idx) => (
                            <option key={`${b.value}-${idx}`} value={b.value}>
                              {b.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className={labelClass}>Vehicle Status</label>
                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        <option value="All">All</option>
                        <option value="Active">Active</option>
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code / Number</label>
                      <input
                        type="text"
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle code/number"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Type</label>
                      <select
                        name="vehicleTypeIndex"
                        value={filters.vehicleTypeIndex}
                        onChange={handleChange}
                        className={`${inputClass} ${
                          lockVehicleType ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={lockVehicleType || isBusy}
                        title={lockVehicleType ? "Vehicle Type locked by navigation" : ""}
                      >
                        {vehicleTypeOptions.map((t, idx) => (
                          <option key={`${t.value}-${idx}`} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Sub Type</label>
                      <select
                        name="vehSubTypeId"
                        value={filters.vehSubTypeId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy || isSelect(filters.vehicleTypeIndex)}
                      >
                        {vehicleSubTypeOptions.map((s, idx) => (
                          <option key={`${s.value}-${idx}`} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Store</label>
                      <select
                        name="storeId"
                        value={filters.storeId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        {storeOptions.map((s, idx) => (
                          <option key={`${s.value}-${idx}`} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Make</label>
                      <input
                        type="text"
                        name="make"
                        value={filters.make}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter make"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Model</label>
                      <input
                        type="text"
                        name="model"
                        value={filters.model}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter model"
                        disabled={isBusy}
                      />
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : subTypeLoading
                        ? "Loading vehicle sub types..."
                        : "Ready"}
                    </div>

                    <div className="flex gap-4 flex-wrap">
                      <button
                        onClick={handleReset}
                        type="button"
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleView}
                        type="button"
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaEye />
                        View
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaSearch />
                        Search
                      </button>

                      <button
                        onClick={handlePrint}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        disabled={isBusy}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        isDark ? "text-gray-200" : "text-gray-800"
                      }`}
                    >
                      No Records
                    </h3>
                    <p className={`${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Use filters and click Search.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={inputHeadClass}>
                        <tr>
                          <th className="px-4 py-4 text-center font-semibold">S.No</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Type</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Number</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Make</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Model</th>
                          <th className="px-4 py-4 text-center font-semibold">Chassis Number</th>
                          <th className="px-4 py-4 text-center font-semibold">Invoice Number</th>
                          <th className="px-4 py-4 text-center font-semibold">Purchase Date</th>
                          <th className="px-4 py-4 text-center font-semibold">Purchase Value</th>
                          <th className="px-4 py-4 text-center font-semibold">Cum HMR</th>
                          <th className="px-4 py-4 text-center font-semibold">Cum KMR</th>
                          <th className="px-4 py-4 text-center font-semibold">Branch</th>
                          <th className="px-4 py-4 text-center font-semibold">Print</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {rows.map((r, idx) => (
                          <tr
                            key={r.fld_vehicleid ?? idx}
                            className={`${
                              isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                            } transition-colors duration-200`}
                          >
                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {(page - 1) * pageSize + idx + 1}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_vehicletypename ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_vehicle_code ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_vehicle_name ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_vehicle_regno ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_vehiclemake ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_vehicle_model ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_Chassisno ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_invoiceno ?? ""}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {fmtDate(r.fld_purchaseddate)}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {fmtNum(r.fld_purchased_amt)}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {fmtNum(r.fld_cumHMR)}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {fmtNum(r.fld_cumKMR)}
                            </td>

                            <td
                              className={`px-4 py-4 text-sm text-center ${
                                isDark ? "text-gray-300" : "text-gray-900"
                              }`}
                            >
                              {r.fld_branchname ?? ""}
                            </td>

                            <td className="px-4 py-4 text-center">
                              <button
                                type="button"
                                onClick={() => handleRowPrint(r)}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass}`}
                                title="Print"
                              >
                                <FaPrint />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* paging */}
              {total > 0 && (
                <div className="mt-6 flex justify-center items-center gap-3">
                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page - 1)}
                    disabled={isBusy || page <= 1}
                    type="button"
                  >
                    Prev
                  </button>

                  <div
                    className={`px-4 py-2 border rounded ${
                      isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"
                    }`}
                  >
                    Page {page} / {totalPages}
                  </div>

                  <button
                    className={`px-4 py-2 border rounded ${
                      isDark
                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                        : "border-gray-300 text-gray-700 hover:bg-gray-100"
                    } ${isBusy || page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => goPage(page + 1)}
                    disabled={isBusy || page >= totalPages}
                    type="button"
                  >
                    Next
                  </button>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : subTypeLoading
                    ? "Loading vehicle subtypes..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}